import React from "react";
import { SurfaceCard } from "../../ui/SurfaceCard";
import { Title } from "../../ui/Title";
import { EditableText } from "../EditableText";
import { AnimatedField } from "../../animations/AnimatedField";
import { resolveText } from "../../utils/resolveText";
import { SortableList } from "../SortableList";

export function BulletListRenderer({
  element,
  editing,
  onChange,
  onReorder,
  playing,
  isPaused,
  currentTimeSec,
}) {
  const title = element.title ? resolveText(element.title) : null;
  const titleBox = element.title?.boxAnimation ?? null;
  const titleExit = element.title?.boxExitAnimation ?? null;
  const items = element.items || [];

  return (
    <SurfaceCard compact>
      <div style={{ display: "grid", gap: "var(--space-4)" }}>
        {title !== null && (
          <AnimatedField
            boxAnimation={titleBox}
            boxExitAnimation={titleExit}
            playing={playing}
            isPaused={isPaused}
            currentTimeSec={currentTimeSec}
          >
            <Title size="xl">
              <EditableText
                value={title}
                editing={editing}
                onChange={(v) => onChange?.("title", v)}
              />
            </Title>
          </AnimatedField>
        )}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--space-3)",
          }}
        >
          <SortableList
            editing={editing}
            items={items}
            onReorder={(newItems) => onReorder?.("items", newItems)}
            direction="vertical"
            handleStyle={{
              top: "50%",
              left: -10,
              transform: "translateY(-50%)",
            }}
            renderItem={(item, i) => {
              const text = resolveText(item);
              const itemBox =
                typeof item === "object" && item !== null
                  ? (item.boxAnimation ?? item.text?.boxAnimation ?? null)
                  : null;
              const itemExit =
                typeof item === "object" && item !== null
                  ? (item.boxExitAnimation ?? item.text?.boxExitAnimation ?? null)
                  : null;
              return (
                <AnimatedField
                  boxAnimation={itemBox}
                  boxExitAnimation={itemExit}
                  playing={playing}
                  isPaused={isPaused}
                  currentTimeSec={currentTimeSec}
                >
                  <div
                    style={{
                      display: "flex",
                      gap: "var(--space-3)",
                      alignItems: "center",
                      minHeight: "1.5em",
                    }}
                  >
                    <span
                      style={{
                        flexShrink: 0,
                        width: "6px",
                        height: "6px",
                        borderRadius: "50%",
                        background: "var(--color-text-accent)",
                      }}
                    />
                    <span
                      style={{
                        flex: 1,
                        fontFamily: "var(--font-body)",
                        fontSize: "var(--text-base)",
                        color: "var(--color-text-primary)",
                        lineHeight: 1.6,
                      }}
                    >
                      <EditableText
                        value={text}
                        editing={editing}
                        onChange={(v) => onChange?.(`items.${i}`, v)}
                      />
                    </span>
                    {editing && (
                      <button
                        type="button"
                        aria-label="Delete item"
                        onClick={() =>
                          onReorder?.(
                            "items",
                            items.filter((_, idx) => idx !== i)
                          )
                        }
                        style={{
                          flexShrink: 0,
                          width: 20,
                          height: 20,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: "none",
                          borderRadius: "var(--radius-sm, 4px)",
                          background: "transparent",
                          color: "var(--color-text-muted, #94a3b8)",
                          cursor: "pointer",
                          fontSize: 16,
                          lineHeight: 1,
                          padding: 0,
                        }}
                      >
                        ×
                      </button>
                    )}
                  </div>
                </AnimatedField>
              );
            }}
          />
          {editing && (
            <button
              type="button"
              aria-label="Add item"
              onClick={() => onReorder?.("items", [...items, "New item"])}
              style={{
                width: "100%",
                padding: "6px",
                border: "1px dashed var(--color-border-subtle, rgba(255,255,255,0.12))",
                borderRadius: "var(--radius-sm, 4px)",
                background: "transparent",
                color: "var(--color-text-muted, #94a3b8)",
                cursor: "pointer",
                fontSize: "var(--text-sm, 13px)",
                fontFamily: "var(--font-body, sans-serif)",
                textAlign: "center",
              }}
            >
              + Add item
            </button>
          )}
        </div>
      </div>
    </SurfaceCard>
  );
}

export default BulletListRenderer;
